import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Building2, Users, Briefcase, FileText, Receipt, Inbox, Sparkles, BadgeAlert, ArrowUpRight, Loader2, CreditCard } from "lucide-react";
import { API_BASE, useAuth, formatApiError } from "../components/AuthContext";

const STAT_TILES = [
  { key: "orgs", label: "Organizations", icon: Building2 },
  { key: "users", label: "Users", icon: Users },
  { key: "clients", label: "Clients", icon: Briefcase },
  { key: "jobs", label: "Jobs", icon: Briefcase },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "triaged_emails", label: "Triaged emails", icon: Inbox },
  { key: "proposals_pending", label: "Pending proposals", icon: Sparkles },
  { key: "email_suppressions", label: "Suppressions", icon: BadgeAlert },
];

export default function SuperAdmin() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, u] = await Promise.all([
        axios.get(`${API_BASE}/superadmin/stats`),
        axios.get(`${API_BASE}/superadmin/orgs`),
        axios.get(`${API_BASE}/superadmin/users`),
      ]);
      setStats(s.data); setOrgs(o.data); setUsers(u.data);
    } catch (err) {
      toast.error(formatApiError(err));
      if (err?.response?.status === 403) navigate("/dashboard");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { reload(); }, [reload]);

  const promote = async (uid) => {
    if (!window.confirm("Grant superadmin to this user?")) return;
    try { await axios.post(`${API_BASE}/superadmin/users/${uid}/promote`); toast.success("Promoted"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const demote = async (uid) => {
    if (!window.confirm("Remove superadmin from this user?")) return;
    try { await axios.post(`${API_BASE}/superadmin/users/${uid}/demote`); toast.success("Demoted"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!state?.user?.is_superadmin) {
    return (
      <div className="px-10 py-16 max-w-3xl">
        <div className="card p-8 text-center">
          <ShieldCheck size={28} className="mx-auto mb-2" style={{ color: "var(--ink-3)" }} />
          <div className="font-heading text-2xl mb-1">Superadmin only</div>
          <p className="text-[var(--ink-3)] text-sm">You don't have access to this area.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-10 py-12 max-w-[1500px]" data-testid="superadmin-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2 inline-flex items-center gap-1.5">
            <ShieldCheck size={12} /> Platform operator
          </div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Super<em className="italic" style={{ color: "var(--accent)" }}>admin</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Cross-tenant visibility. Use sparingly — every action here bypasses normal org isolation.
          </p>
        </div>
        <button
          onClick={() => navigate("/superadmin/billing")}
          data-testid="superadmin-billing-link"
          className="btn-secondary"
        >
          <CreditCard size={14} /> Platform billing
        </button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3 mb-10" data-testid="superadmin-stats">
        {STAT_TILES.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.key} className="card p-3" data-testid={`stat-${t.key}`}>
              <div className="flex items-center justify-between mb-2">
                <Icon size={14} style={{ color: "var(--ink-3)" }} />
              </div>
              <div className="font-mono text-2xl text-[var(--ink)]" style={{ fontWeight: 500 }}>
                {loading ? "—" : (stats?.[t.key] ?? 0)}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mt-1">{t.label}</div>
            </div>
          );
        })}
      </div>

      {/* Orgs */}
      <section className="mb-10">
        <h2 className="font-heading text-2xl mb-3" style={{ fontWeight: 500 }}>Organizations</h2>
        <div className="card overflow-hidden">
          <table className="w-full text-[13px]" data-testid="orgs-table">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                <th className="text-left p-3 overline text-[10px]">Org name</th>
                <th className="text-left p-3 overline text-[10px]">Plan</th>
                <th className="text-right p-3 overline text-[10px]">Users</th>
                <th className="text-right p-3 overline text-[10px]">Clients</th>
                <th className="text-right p-3 overline text-[10px]">Jobs</th>
                <th className="text-left p-3 overline text-[10px]">Created</th>
              </tr>
            </thead>
            <tbody>
              {orgs.length === 0 && !loading && (
                <tr><td colSpan={6} className="p-6 text-center text-[var(--ink-3)]">No organizations yet.</td></tr>
              )}
              {orgs.map((o) => (
                <tr key={o.org_id} data-testid={`org-row-${o.org_id}`} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td className="p-3"><b>{o.name}</b><br/><span className="text-[10px] text-[var(--ink-3)] font-mono">{o.org_id}</span></td>
                  <td className="p-3"><span className="badge" style={{ background: "var(--subtle)" }}>{o.plan}</span></td>
                  <td className="p-3 text-right font-mono">{o.user_count}</td>
                  <td className="p-3 text-right font-mono">{o.client_count}</td>
                  <td className="p-3 text-right font-mono">{o.job_count}</td>
                  <td className="p-3 text-[var(--ink-3)] text-[12px]">{o.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Users */}
      <section>
        <h2 className="font-heading text-2xl mb-3" style={{ fontWeight: 500 }}>Users</h2>
        <div className="card overflow-hidden">
          <table className="w-full text-[13px]" data-testid="users-table">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                <th className="text-left p-3 overline text-[10px]">Email</th>
                <th className="text-left p-3 overline text-[10px]">Name</th>
                <th className="text-left p-3 overline text-[10px]">Org</th>
                <th className="text-left p-3 overline text-[10px]">Source</th>
                <th className="text-left p-3 overline text-[10px]">Superadmin</th>
                <th className="text-right p-3 overline text-[10px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !loading && (
                <tr><td colSpan={6} className="p-6 text-center text-[var(--ink-3)]">No users.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td className="p-3"><b>{u.email}</b></td>
                  <td className="p-3">{u.name}</td>
                  <td className="p-3 text-[10px] font-mono text-[var(--ink-3)]">{u.org_id || "—"}</td>
                  <td className="p-3"><span className="badge" style={{ background: "var(--subtle)" }}>{u.source}</span></td>
                  <td className="p-3">{u.is_superadmin ? <ShieldCheck size={14} style={{ color: "var(--moss)" }} /> : <span className="text-[var(--ink-3)]">—</span>}</td>
                  <td className="p-3 text-right">
                    {u.is_superadmin
                      ? <button onClick={() => demote(u.user_id)} className="text-[11px] text-[var(--ink-3)] hover:text-[var(--accent)]" data-testid={`demote-${u.user_id}`}>Revoke</button>
                      : <button onClick={() => promote(u.user_id)} className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1" data-testid={`promote-${u.user_id}`}>Grant superadmin <ArrowUpRight size={11}/></button>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {loading && <div className="text-center py-4 text-[var(--ink-3)] text-[12px]"><Loader2 size={14} className="inline animate-spin mr-1"/> Loading platform data…</div>}
    </div>
  );
}
